import { useState, useEffect, useCallback } from 'react';
import { rateEvents } from '../lib/rateEvents';
import { API_BASE_URL } from '../lib/api';

export const EXCHANGE_RATE_REFRESH_INTERVAL_MS = 10 * 1000;

interface UseExchangeRateOptions {
    autoLoad?: boolean;
    refreshInterval?: number;
    defaultRate?: number;
}

export const useExchangeRate = (options: UseExchangeRateOptions = {}) => {
    const {
        autoLoad = true,
        refreshInterval = EXCHANGE_RATE_REFRESH_INTERVAL_MS, // 10 секунд
        defaultRate = 100,
    } = options;

    const [rate, setRate] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchRate = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${API_BASE_URL}/public/rate/RUB/USDT`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            const newRate = data.rate;

            if (typeof newRate === 'number' && newRate > 0) {
                // Обновляем только если курс изменился
                setRate(prevRate => {
                    if (prevRate === newRate) {
                        return prevRate;
                    }

                    rateEvents.emit(newRate);
                    return newRate;
                });

                return newRate;
            } else {
                throw new Error('Неверный формат курса');
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Неизвестная ошибка';
            console.error('Ошибка загрузки курса:', errorMessage);
            setError(errorMessage);

            setRate(prevRate => prevRate === null ? defaultRate : prevRate);

            return null;
        } finally {
            setLoading(false);
        }
    }, [defaultRate]);

    useEffect(() => {
        const handleRateUpdate = (newRate: number) => {
            setRate(newRate);
            setError(null);
        };

        rateEvents.on(handleRateUpdate);

        return () => {
            rateEvents.off(handleRateUpdate);
        };
    }, []);

    useEffect(() => {
        if (autoLoad) {
            fetchRate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]);

    useEffect(() => {
        if (!autoLoad || !refreshInterval || refreshInterval <= 0) {
            return;
        }

        const intervalId = setInterval(() => {
            fetchRate();
        }, refreshInterval);

        return () => {
            clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad, refreshInterval]);

    return {
        rate,
        loading,
        error,
        refresh: fetchRate,
    };
};
